import { Checkbox } from '../../../components/ui/Checkbox';

import Icon from '../../../components/AppIcon';

const ApplicationSettings = ({ formData, onChange, errors }) => {
  const screeningQuestions = [
    { id: 'experience', label: 'Years of relevant experience', enabled: true },
    { id: 'availability', label: 'Notice period or availability to join', enabled: true },
    { id: 'salary', label: 'Current and expected salary', enabled: false },
    { id: 'relocation', label: 'Willingness to relocate', enabled: false },
    { id: 'portfolio', label: 'Portfolio or work samples', enabled: false }
  ];

  const documentRequirements = [
    { id: 'resume', label: 'Resume/CV', required: true, enabled: true },
    { id: 'cover-letter', label: 'Cover Letter', required: false, enabled: false },
    { id: 'portfolio', label: 'Portfolio/Work Samples', required: false, enabled: false },
    { id: 'certificates', label: 'Relevant Certificates', required: false, enabled: false }
  ];

  const toggleScreeningQuestion = (questionId) => {
    const questions = formData?.screeningQuestions || screeningQuestions;
    const updated = questions?.map(q => 
      q?.id === questionId ? { ...q, enabled: !q?.enabled } : q
    );
    onChange('screeningQuestions', updated);
  };

  const toggleDocumentRequirement = (docId) => {
    const documents = formData?.documentRequirements || documentRequirements;
    const updated = documents?.map(d => 
      d?.id === docId ? { ...d, enabled: !d?.enabled } : d
    );
    onChange('documentRequirements', updated);
  };

  const toggleDocumentRequired = (docId) => {
    const documents = formData?.documentRequirements || documentRequirements;
    const updated = documents?.map(d => 
      d?.id === docId ? { ...d, required: !d?.required } : d
    );
    onChange('documentRequirements', updated);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
          <Icon name="Settings" size={20} color="var(--color-warning)" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Application Settings</h2>
          <p className="text-sm text-muted-foreground">Configure application requirements</p>
        </div>
      </div>
      <div className="space-y-8">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground">Screening Questions</label>
            <span className="text-xs text-muted-foreground">
              {(formData?.screeningQuestions || screeningQuestions)?.filter(q => q?.enabled)?.length} selected
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Ask specific questions to pre-screen candidates before reviewing applications
          </p>
          <div className="space-y-3">
            {(formData?.screeningQuestions || screeningQuestions)?.map((question) => (
              <div key={question?.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <Checkbox
                  label={question?.label}
                  checked={question?.enabled}
                  onChange={() => toggleScreeningQuestion(question?.id)}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground">Document Requirements</label>
            <span className="text-xs text-muted-foreground">
              {(formData?.documentRequirements || documentRequirements)?.filter(d => d?.enabled)?.length} required
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Specify which documents candidates must submit with their application
          </p>
          <div className="space-y-3">
            {(formData?.documentRequirements || documentRequirements)?.map((doc) => (
              <div key={doc?.id} className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Checkbox
                    label={doc?.label}
                    checked={doc?.enabled}
                    onChange={() => toggleDocumentRequirement(doc?.id)}
                  />
                  {doc?.enabled && doc?.id !== 'resume' && (
                    <button
                      type="button"
                      onClick={() => toggleDocumentRequired(doc?.id)}
                      className={`px-2 py-1 rounded text-xs font-medium transition-smooth ${
                        doc?.required
                          ? 'bg-error/10 text-error' :'bg-muted text-muted-foreground'
                      }`}
                    >
                      {doc?.required ? 'Required' : 'Optional'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <label className="text-sm font-medium text-foreground">Additional Settings</label>
          <div className="space-y-3">
            <Checkbox
              label="Send automatic confirmation email to applicants"
              description="Candidates will receive an email confirming their application submission"
              checked={formData?.autoConfirmation !== false}
              onChange={(e) => onChange('autoConfirmation', e?.target?.checked)}
            />
            <Checkbox
              label="Allow candidates to save application as draft"
              description="Candidates can save incomplete applications and complete them later"
              checked={formData?.allowDraft !== false}
              onChange={(e) => onChange('allowDraft', e?.target?.checked)}
            />
            <Checkbox
              label="Notify me immediately when someone applies"
              description="Receive real-time email notifications for new applications"
              checked={formData?.instantNotification}
              onChange={(e) => onChange('instantNotification', e?.target?.checked)}
            />
          </div>
        </div>

        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex gap-3">
            <Icon name="Lightbulb" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <div className="space-y-1 text-sm">
              <p className="font-medium text-foreground">Application Best Practices</p>
              <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                <li>Keep screening questions relevant and concise (3-5 questions maximum)</li>
                <li>Only request documents that are essential for initial screening</li>
                <li>Enable draft saving to reduce application abandonment rates</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationSettings;